/* eslint-disable max-len */
import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Grid } from '@material-ui/core';
import ReCaptcha from 'react-google-recaptcha';
import styled from 'styled-components';
import FormController from '../../components/globals/FormController';
import PasswordHelper from '../../components/globals/passwordHelper';
import PasswordInput from '../../components/globals/PasswordInput';
import GlobalTheme from '../../styled/GlobalTheme';
import {
  CreateAccountButton,
  NameFormController,
} from './CreateAccount.styled';
import useGetUserFormData from './hooks/useGetUserFormData';
import useDidUpdateEffect from '../../hooks/useDidUpdateEffect';
import { SEVERITY } from '../../utils/constants/alerts';
import CaptchaProvider from '../../services/entities/CaptchaProvider';
import { GOOGLE_SITE_KEY } from '../../utils/constants/globals';
import { AlertsContext } from '../../contexts/AlertsContext';

const Form = styled.form`
  width: 100%;
`;

const StyledCreateAccountButton = styled(CreateAccountButton)`
  && {
    width: 100%;
    max-width: 360px;
    margin-bottom: 20px;
  }
`;

const ButtonContainer = styled(Grid)`
  && {
    text-align: center;
  }
`;

const StyledNameFormController = styled(NameFormController)`
  && {
    margin: 0;
    opacity: ${({ opacity }) => (opacity || '1')};
  }
`;

const StyledFormController = styled(FormController)`
  && {
    margin: 0;
    opacity: ${({ opacity }) => (opacity || '1')};
  }
`;

const StyledPasswordInput = styled(PasswordInput)`
  && {
    margin: 0;
    opacity: ${({ opacity }) => (opacity || '1')};
  }
`;

const StyledPasswordHelper = styled(PasswordHelper)`
  opacity: ${({ opacity }) => (opacity || '1')};
`;

const ContainerCaptcha = styled.div`
  margin-top: 25px;
  div > div {
    margin: auto;
  }
`;

const CreateAccountForm = ({ wantsFree, className, planSelected }) => {
  const {
    user,
    setUser,
    setIsPasswordValid,
    handleChange,
    signUp,
    isEmpty,
  } = useGetUserFormData();

  const { showAlert, showError } = useContext(AlertsContext);
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [currentToken, setCurrentToken] = useState('');
  const recaptchaRef = React.createRef();

  useDidUpdateEffect(() => {
    setUser({
      ...user,
      wantsFree,
    });
  }, [wantsFree]);

  const onChange = (token) => {
    if (!token) {
      setLoading(false);
      setButtonDisabled(true);
    }
    setCurrentToken(token);
    setButtonDisabled(false);
  };

  const createAccount = async () => {
    setLoading(true);
    const validate = await CaptchaProvider.validateCaptcha(currentToken);

    if (validate.data?.validateCaptcha?.isValid) {
      try {
        const response = await signUp();
        if (!response) {
          setButtonDisabled(false);
          setLoading(false);
        }
      } catch (e) {
        setButtonDisabled(false);
        setLoading(false);
        showError(intl.formatMessage({
          id: 'alert.error.loginEmail',
          defaultMessage: 'Your mail or password is incorrect. Please write again.',
        }));
      }
    } else {
      showAlert(
        intl.formatMessage({
          id: 'k.login.message.captcha.error',
          defaultMessage: 'The page will reload to display a new checker.',
        }), SEVERITY.WARNING,
      );
      recaptchaRef.current.reset();
    }
  };

  return (
    <Form
      className={className}
      planSelected={planSelected}
    >
      <Grid
        container
        direction="row"
        justify="space-between"
        spacing={window.innerWidth < GlobalTheme.breakpoints.values.lg ? 0 : 4}
      >
        <Grid item xs={12} md={5} lg={6}>
          <StyledNameFormController
            label={(
              <FormattedMessage
                id="onboarding.creteAccount.firstname"
                description="Create account input first name"
                defaultMessage="First name"
              />
            )}
            value={user.firstName}
            placeholder="Liam"
            onChange={handleChange}
            id="firstName"
            showRequired
            showRequiredError={isEmpty.firstName}
            opacity={planSelected ? '1' : '0.25'}
          />
        </Grid>
        <Grid item xs={12} md={5} lg={6}>
          <StyledNameFormController
            label={(
              <FormattedMessage
                id="onboarding.creteAccount.lastname"
                description="Create account input last name"
                defaultMessage="Last name"
              />
            )}
            value={user.lastName}
            placeholder="Smith"
            onChange={handleChange}
            id="lastName"
            showRequired
            showRequiredError={isEmpty.lastName}
            opacity={planSelected ? '1' : '0.25'}
          />
        </Grid>
      </Grid>
      <Grid
        container
        direction="row"
        justify="space-between"
        spacing={window.innerWidth < GlobalTheme.breakpoints.values.lg ? 0 : 4}
      >
        <Grid item xs={12} md={5} lg={6}>
          <StyledFormController
            label={(
              <FormattedMessage
                id="onboarding.creteAccount.email"
                description="Create account input email"
                defaultMessage="Email"
              />
            )}
            value={user.email}
            placeholder="smith@mail.com"
            onChange={handleChange}
            id="email"
            showRequired
            showRequiredError={isEmpty.email}
            opacity={planSelected ? '1' : '0.25'}
          />
        </Grid>

        <Grid item xs={12} md={5} lg={6}>
          <StyledPasswordInput
            label={(
              <FormattedMessage
                id="onboarding.creteAccount.password"
                description="Create account input password"
                defaultMessage="Password"
              />
            )}
            value={user.password}
            type="password"
            onChange={handleChange}
            id="password"
            showRequired
            showRequiredError={isEmpty.password}
            opacity={planSelected ? '1' : '0.25'}
          />
          {user.password && (
            <StyledPasswordHelper
              password={user.password}
              setIsValid={setIsPasswordValid}
              opacity={planSelected ? '1' : '0.25'}
            />
          )}
        </Grid>
      </Grid>
      <ContainerCaptcha>
        <ReCaptcha
          ref={recaptchaRef}
          sitekey={GOOGLE_SITE_KEY}
          size="normal"
          hl={intl.locale}
          onChange={onChange}
        />
      </ContainerCaptcha>
      <ButtonContainer item>
        <StyledCreateAccountButton
          handlerClick={createAccount}
          disabled={!planSelected || buttonDisabled}
          isLoading={loading}
        >
          <FormattedMessage
            id="onboarding.creteAccount.signUp"
            defaultMessage="Sign up!"
          />
        </StyledCreateAccountButton>
      </ButtonContainer>
    </Form>
  );
};

CreateAccountForm.propTypes = {
  wantsFree: PropTypes.bool.isRequired,
  className: PropTypes.string,
  planSelected: PropTypes.string,
};

CreateAccountForm.defaultProps = {
  className: '',
  planSelected: '',
};

export default CreateAccountForm;
